import { toast } from '@/hooks/use-toast';
import Cookies from 'js-cookie';

export async function handleAPIResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: { message?: string; error?: string } = { message: 'An error occurred' };
    
    try {
      errorData = await response.json();
    } catch (parseError) {
      console.error('Failed to parse error response:', parseError);
      // Use default error message if parsing fails
    }

    const errorMessage = errorData.message || errorData.error || 'An error occurred';

    switch (response.status) {
      case 401:
        // Clear the expired/invalid token
        Cookies.remove('token');

        toast({
          title: 'Authentication Error',
          description: 'Please log in to continue',
          variant: 'error',
        });

        // Add delay before redirect to ensure toast is visible
        await new Promise(resolve => setTimeout(resolve, 2000));

        window.location.href = '/login';
        break;
      case 403:
        toast({
          title: 'Access Denied',
          description: "You don't have permission to perform this action",
          variant: 'error',
        });
        break;
      case 404:
        toast({
          title: 'Not Found',
          description: 'The requested resource was not found',
          variant: 'error',
        });
        break;
      case 500:
      case 501:
      case 502:
      case 503:
      case 504:
        toast({
          title: 'Server Error',
          description: 'Our servers are experiencing issues. Please try again later.',
          variant: 'error',
        });
        break;
      default:
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'error',
        });
    }

    // Create a more descriptive error
    const error = new Error(errorMessage);
    error.name = `HTTP${response.status}`;
    throw error;
  }

  try {
    const result = await response.json();
    return result.data;
  } catch (parseError) {
    console.error('Failed to parse response:', parseError);
    throw new Error('Failed to parse server response');
  }
}

interface HeaderOptions {
  addContentType?: boolean;
}

export function getAuthHeaders(options: HeaderOptions = { addContentType: true }) {
  const headers: Record<string, string> = {};
  const token = Cookies.get('token');

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (options.addContentType) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}
